import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import App from "./app";
import { msalBaseConfig, metadataUrl } from "./authConfig";
import { ThemeProvider, CssBaseline } from "@mui/material";
// theme.js declares Inter as the app font but nothing ever loaded it, so every
// page silently fell back to Helvetica. It matters more now that the stock
// dashboard renders natively instead of inside a report that inlined its own copy.
import "@fontsource/inter/400.css";
import "@fontsource/inter/600.css";
import theme from "./theme";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";
import { clearStaleInteractionState, restorePostAuthRedirect, looksLikeAuthResponse, noteAuthReturnFailed } from "./utils/authRedirect";
import { START_PATH } from "./utils/landing";
import { captureAttribution } from "./utils/attribution";

export let msalInstance = null;

async function getMsalInstance() {
  if (window.__msalInstance) return window.__msalInstance;

  console.log("[BOOT] Fetching CIAM metadata:", metadataUrl);
  const res = await fetch(metadataUrl, { cache: "no-store" });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Metadata fetch failed (${res.status})\n${metadataUrl}\n${text.slice(0, 300)}`);
  }
  const metadataJson = await res.json();

  const msalConfig = {
    ...msalBaseConfig,
    auth: {
      ...msalBaseConfig.auth,
      authorityMetadata: JSON.stringify(metadataJson), // JSON string, not URL
    },
  };

  console.log("[BOOT] authority:", msalConfig.auth.authority);
  console.log("[BOOT] metadata keys:", Object.keys(metadataJson));

  const instance = new PublicClientApplication(msalConfig);

  // ✅ REQUIRED in msal-browser@4: initialize before any other API
  await instance.initialize();

  // Handle redirect result once. `freshLogin` = this page load IS the return leg of a redirect
  // sign-in (result carries an account); a plain reload of an existing session does not set it.
  let freshLogin = false;
  // Read before handleRedirectPromise clears the URL: did Microsoft just send a result back?
  const returningFromSignIn = looksLikeAuthResponse();
  await instance.handleRedirectPromise()
    .then((result) => {
      if (result?.account) { instance.setActiveAccount(result.account); freshLogin = true; }
      else {
        // A result came back but produced no account: the pre-redirect state was lost (Safari
        // private windows, on the longer sign-up flow). /login says so instead of looking unchanged.
        if (returningFromSignIn) noteAuthReturnFailed("no_result");
        const accts = instance.getAllAccounts();
        if (accts.length > 0) instance.setActiveAccount(accts[0]);
      }
    })
    .catch((e) => {
      console.error("[BOOT] handleRedirectPromise error:", e);
      if (returningFromSignIn) noteAuthReturnFailed(e?.errorCode || e?.name);
    });

  // Redirect handling is done for this page load, so any surviving "interaction in progress"
  // flag is stale — left behind by an interrupted redirect. Left in place it makes every
  // acquireTokenSilent fail and (before the api.js fix) re-redirect forever, and because it's
  // cached per-browser it survived reloads. Clearing it here lets a poisoned browser self-heal
  // on the next load instead of needing localStorage wiped by hand.
  clearStaleInteractionState();

  // Send the user back where they started. redirectUri is pinned to the bare origin and
  // navigateToLoginRequestUrl is false, so MSAL always returns to "/" — restoring the saved
  // path here (before React mounts, so BrowserRouter picks it up on first render) is what
  // keeps a sign-in from /memory on /memory.
  //
  // Only on that return leg. A signed-out visit to a protected page saves its path on the way to
  // /login; restoring it on any later load (the save lives 5 minutes) hijacked the next page someone
  // opened, e.g. /connect rewritten to "/" and bounced to /login again. Unconsumed, the save simply
  // waits for the sign-in it was made for (and /login re-saves it when the button is pressed).
  const restored = (freshLogin || returningFromSignIn) ? restorePostAuthRedirect() : null;
  if (restored) console.log("[BOOT] restored post-auth path:", restored);
  // A redirect sign-in with NO saved deep link lands on the bare origin — which is the analytics
  // app. Nothing about that user said "analytics"; hand them to /start, which decides from the
  // account (the agent by default, analytics for accounts that have projects).
  else if (freshLogin && window.location.pathname === "/") {
    window.history.replaceState(null, "", START_PATH);
    console.log("[BOOT] fresh sign-in, no saved path →", START_PATH);
  }

  // Keep active account in sync
  instance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      instance.setActiveAccount(event.payload.account);
      console.log("[MSAL] LOGIN_SUCCESS:", event.payload.account.username);
    }
  });

  window.__msalInstance = instance;
  return instance;
}

async function bootstrap() {
  // Before MSAL: the sign-in redirect's return leg carries no query string, so the channel tag on
  // the link someone arrived by has to be read on this first load or it is lost.
  captureAttribution();
  msalInstance = await getMsalInstance();

  ReactDOM.createRoot(document.getElementById("root")).render(
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <ImpersonationProvider>
            <App />
          </ImpersonationProvider>
        </BrowserRouter>
      </ThemeProvider>
    </MsalProvider>
  );
}

bootstrap().catch((e) => {
  console.error("[BOOT ERROR]", e);
  const el = document.getElementById("root");
  if (el) el.innerHTML = `<pre style="padding:16px;color:#b00020;white-space:pre-wrap;">${String(e)}</pre>`;
});
